const APPLE = /mac|iphone|ipad|ipod/iu;

interface Nav {
  userAgentData?: { platform?: string };
  platform?: string;
  userAgent?: string;
}

/** Apple platforms (macOS, iOS, iPadOS, which reports itself as a Mac): some conventions differ there. */
export function isApple(nav: Nav = navigator as Nav): boolean {
  return APPLE.test(nav.userAgentData?.platform || nav.platform || "") || APPLE.test(nav.userAgent ?? "");
}

/** Marks the page (`<html data-platform="apple|other">`) so CSS can follow platform conventions. */
export function markPlatform(): void {
  document.documentElement.dataset["platform"] = isApple() ? "apple" : "other";
}

const IOS = /iphone|ipad|ipod/iu;

/** iOS and iPadOS (which reports itself as a Mac, with a touch screen). */
export function isIOS(
  nav: Nav & { maxTouchPoints?: number } = navigator as Nav & { maxTouchPoints?: number },
): boolean {
  const platform = nav.platform ?? "";
  return (
    IOS.test(platform) || IOS.test(nav.userAgent ?? "") || (platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1)
  );
}

/**
 * On iOS, the page isn't zoomed in when a field smaller than 16 px takes the focus (a table's cell's editor, at its
 * text's size: Safari's zooming in on it, and not back, was in the way). `maximum-scale=1` stops only that there:
 * Safari lets you pinch-zoom all the same (since iOS 10), where Android's browsers wouldn't, so it's iOS's alone.
 */
export function keepZoomOnFocus(nav?: Nav & { maxTouchPoints?: number }): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (meta && isIOS(nav) && !meta.content.includes("maximum-scale")) {
    meta.content = `${meta.content}, maximum-scale=1`;
  }
}

/** A phone: a touch screen (a finger's coarse pointer) narrower than a tablet's. What's made there suits it (a
 *  document in free flow). */
export const onPhone = (): boolean =>
  globalThis.matchMedia("(pointer: coarse)").matches && globalThis.matchMedia("(max-width: 640px)").matches;

/** Asks for a symbol's plain (text) look, not the emoji some systems draw for ↩. */
const TEXT_STYLE = "\uFE0E";

/** The keys that send what's written (a comment), as each platform writes them: ⌘↩ on Apple's, Ctrl+Enter elsewhere. */
export const sendKeys = (nav: Nav = navigator as Nav): string => (isApple(nav) ? `⌘↩${TEXT_STYLE}` : "Ctrl+Enter");
